import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiClient } from "../../lib/api-client";
import { formatBRL, formatDateTimeSaoPaulo } from "../../lib/currency";

interface OrderStatusResponse {
  order: { id: string; order_number: string; status: string; total_cents: number };
  charge: { qr_code_payload: string; qr_code_image_base64: string; expires_at: string; status: string } | null;
}

const POLL_INTERVAL_MS = 5000;

export function CheckoutPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [data, setData] = useState<OrderStatusResponse | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [simulateError, setSimulateError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    let active = true;

    async function poll() {
      const result = await apiClient.get<OrderStatusResponse>(`/consumer/orders/${orderId}`);
      if (active) setData(result);
    }

    void poll();
    const interval = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [orderId]);

  async function handleSimulatePayment() {
    if (!orderId) return;
    setSimulating(true);
    setSimulateError(null);
    try {
      await apiClient.post(`/consumer/orders/${orderId}/simulate`);
      setData((prev) => (prev ? { ...prev, order: { ...prev.order, status: "paid" } } : null));
    } catch {
      setSimulateError("Falha ao simular pagamento.");
    } finally {
      setSimulating(false);
    }
  }

  if (!data) return <p className="text-muted">Carregando pedido…</p>;

  const { order, charge } = data;

  return (
    <div>
      <h1>Pedido {order.order_number}</h1>
      <p>
        <span className={`status-badge ${statusBadgeClass(order.status)}`}>{translateStatus(order.status)}</span>
      </p>
      <p className="price-tag">{formatBRL(order.total_cents)}</p>

      {order.status === "pending" && charge && (
        <div className="card pix-panel">
          <p className="text-muted" style={{ fontSize: "var(--text-sm)" }}>
            Escaneie o QR Code ou copie o código Pix abaixo. Válido até {formatDateTimeSaoPaulo(charge.expires_at)}.
          </p>
          <img className="pix-panel__qr" src={`data:image/png;base64,${charge.qr_code_image_base64}`} alt="QR Code Pix para pagamento" />
          <textarea readOnly value={charge.qr_code_payload} rows={4} className="pix-panel__code" />
          <p className="text-muted" style={{ fontSize: "var(--text-xs)", marginTop: "var(--space-3)" }}>
            Payments by Universal Pagamentos S/A.
          </p>

          <div style={{ marginTop: "var(--space-5)", padding: "var(--space-4)", background: "var(--color-surface-sunken)", borderRadius: "var(--radius-md)", border: "1px dashed var(--color-brass)" }}>
            <h4 style={{ margin: "0 0 var(--space-2)", fontSize: "var(--text-sm)", color: "var(--color-brass)" }}>
              Ambiente de Demonstração / Testes Locais
            </h4>
            <p className="text-muted" style={{ fontSize: "var(--text-xs)", margin: "0 0 var(--space-3)" }}>
              Deseja simular a aprovação instantânea deste pagamento Pix para validar a liberação dos downloads?
            </p>
            {simulateError && <p role="alert" className="notice-error">{simulateError}</p>}
            <button
              type="button"
              className="btn"
              style={{ fontSize: "var(--text-xs)", padding: "8px 16px" }}
              disabled={simulating}
              onClick={handleSimulatePayment}
            >
              {simulating ? "Processando confirmação Pix..." : "✓ Simular Pagamento Pix (Aprovação Imediata)"}
            </button>
          </div>
        </div>
      )}

      {order.status === "paid" && (
        <div className="card" style={{ padding: "var(--space-6)", borderLeft: "4px solid var(--color-success, #22c55e)" }}>
          <h2 style={{ color: "var(--color-success, #15803d)", marginTop: 0 }}>🎉 Pagamento Pix Confirmado!</h2>
          <p style={{ margin: "var(--space-2) 0 var(--space-4)" }}>
            O pagamento do seu pedido foi processado com sucesso e os seus livros já estão disponíveis para download imediato em PDF e EPUB.
          </p>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <a href="/minha-biblioteca" className="btn">
              Acessar Minha Biblioteca & Baixar Livros
            </a>
            <a href="/catalogo" className="btn btn-secondary">
              Continuar Comprando
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

function statusBadgeClass(status: string): string {
  if (status === "paid") return "status-badge--paid";
  if (status === "pending") return "status-badge--pending";
  if (["expired", "cancelled", "blocked", "disputed"].includes(status)) return "status-badge--negative";
  return "";
}

function translateStatus(status: string): string {
  const map: Record<string, string> = {
    pending: "Aguardando pagamento",
    paid: "Pago",
    expired: "Expirado",
    cancelled: "Cancelado",
    refunded: "Reembolsado",
    blocked: "Bloqueado",
    disputed: "Em disputa",
  };
  return map[status] ?? status;
}
